import agentRecommendationsForPlz from '@/assets/agent-recommendations/agentRecommendationsForPlz.json';
import agentsByAgentId from '@/assets/agent-recommendations/agents.json';
import { isPostalCodeInExperimentRegion } from '@/helpers/AgentRecommendations/agentRecommendationExperimentConfig';
import type { AgentRecommendation } from '@/stores/types/propertyValuation';

const MAX_AGENT_RECOMMENDATIONS_PER_POSTAL_CODE = 3;

type AgentRecommendationsByPostalCode = Record<string, string[]>;
type AgentsCatalog = Record<string, AgentRecommendation>;

const plzToAgentIds = agentRecommendationsForPlz as AgentRecommendationsByPostalCode;
const agentsCatalog = agentsByAgentId as AgentsCatalog;

export const normalizePostalCode = (postalCode: unknown): string | null => {
  if (typeof postalCode !== 'string' && typeof postalCode !== 'number') {
    return null;
  }

  const normalizedPostalCode = String(postalCode).trim();
  return /^\d{4}$/.test(normalizedPostalCode) ? normalizedPostalCode : null;
};

const getRandomInteger = (exclusiveMax: number): number => {
  const maxUint32 = 0xffffffff;
  const limit = maxUint32 - (maxUint32 % exclusiveMax);
  const randomValues = new Uint32Array(1);
  let randomValue = 0;

  do {
    globalThis.crypto.getRandomValues(randomValues);
    randomValue = randomValues[0];
  } while (randomValue >= limit);

  return randomValue % exclusiveMax;
};

const shuffleAgentRecommendations = (
  agentRecommendations: AgentRecommendation[],
): AgentRecommendation[] => {
  const shuffledAgentRecommendations = [...agentRecommendations];

  for (let index = shuffledAgentRecommendations.length - 1; index > 0; index -= 1) {
    const randomIndex = getRandomInteger(index + 1);
    [shuffledAgentRecommendations[index], shuffledAgentRecommendations[randomIndex]] = [
      shuffledAgentRecommendations[randomIndex],
      shuffledAgentRecommendations[index],
    ];
  }

  return shuffledAgentRecommendations;
};

const selectAgentRecommendations = (
  agentRecommendations: AgentRecommendation[],
): AgentRecommendation[] => {
  const shuffledAgentRecommendations = shuffleAgentRecommendations(agentRecommendations);
  const selectedAgents: AgentRecommendation[] = [];
  const seenAgencyIds = new Set<string>();

  for (const agent of shuffledAgentRecommendations) {
    if (seenAgencyIds.has(agent.agencyId)) {
      continue;
    }

    selectedAgents.push(agent);
    seenAgencyIds.add(agent.agencyId);

    if (selectedAgents.length === MAX_AGENT_RECOMMENDATIONS_PER_POSTAL_CODE) {
      break;
    }
  }

  return selectedAgents;
};

export const getAgentRecommendationsForPostalCode = (
  postalCode: unknown,
): AgentRecommendation[] => {
  const normalizedPostalCode = normalizePostalCode(postalCode);
  if (!normalizedPostalCode || !isPostalCodeInExperimentRegion(normalizedPostalCode)) {
    return [];
  }

  const agentIds = plzToAgentIds[normalizedPostalCode];
  if (!Array.isArray(agentIds) || agentIds.length === 0) {
    return [];
  }

  const agents: AgentRecommendation[] = [];
  for (const agentId of agentIds) {
    const agent = agentsCatalog[agentId];
    if (agent) {
      agents.push(agent);
    }
  }

  if (agents.length === 0) {
    return [];
  }

  return selectAgentRecommendations(agents);
};
